import { useState } from "react";
import type {
  VisualPayload,
  SeasonOverview,
  MoveChange,
  NewMovesGroup,
  CupSlot,
} from "../../types";
import CardShell from "./_CardShell";
import {
  SeasonHeaderStrip,
  SeasonSourceLink,
  MoveChangeRow,
  NewMoveChip,
  CupWeekBlock,
  groupCupsByWeek,
  CollapsibleList,
} from "./_seasonShared";

interface Props { payload: VisualPayload }

/**
 * Comprehensive season overview card.
 *
 * Renders the season header strip + every populated section the backend
 * emits.  Each dense list (move changes, new moves, cup rotation) has
 * an independent expand/collapse toggle: collapsed shows the first 5
 * entries, expanded shows all.  Mobile-friendly because the user can
 * keep just the sections they're interested in expanded.
 *
 * Only the season_summary intent populates ALL sections.  The other
 * 4 season intents (cup_schedule, cup_rules, move_change, new_moves)
 * each have their own dedicated card component now.
 */
export default function SeasonSummaryCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const overview = (sections.find((s) => s.section === "season_overview") ??
    {}) as SeasonOverview & { section?: string };
  const moveChangesSec = sections.find((s) => s.section === "move_changes");
  const newMovesSec = sections.find((s) => s.section === "new_move_acquisitions");
  const cupScheduleSec = sections.find((s) => s.section === "cup_schedule");
  const rankRewardsSec = sections.find((s) => s.section === "rank_rewards");
  const notesSec = sections.find((s) => s.section === "notes");

  const moveChanges = (moveChangesSec?.items as MoveChange[] | undefined) ?? [];
  const newMovesGrouped = (newMovesSec?.items_grouped as NewMovesGroup[] | undefined) ?? [];
  const cupSchedule = (cupScheduleSec?.items as CupSlot[] | undefined) ?? [];
  const rankRewards = rankRewardsSec?.summary as string | null | undefined;
  const notes = notesSec?.text as string | null | undefined;

  // Per-section expand state — start collapsed so the initial render
  // fits comfortably on mobile.
  const [expandMoves, setExpandMoves] = useState(false);
  const [expandNewMoves, setExpandNewMoves] = useState(false);
  const [expandCups, setExpandCups] = useState(false);

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-indigo-500/20"
    >
      <SeasonHeaderStrip overview={overview} />

      {/* ── Move balance changes ──────────────────────────────────────── */}
      {moveChanges.length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
            Move balance ({moveChanges.length})
          </p>
          <CollapsibleList
            items={moveChanges}
            initial={5}
            itemNoun="more moves"
            expanded={expandMoves}
            onToggle={() => setExpandMoves((v) => !v)}
            renderItem={(c, i) => <MoveChangeRow key={i} c={c} />}
          />
        </div>
      )}

      {/* ── New moves learned (grouped by Pokémon) ────────────────────── */}
      {newMovesGrouped.length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
            New moves ({overview.new_move_count ?? 0} across {overview.pokemon_with_new_moves ?? newMovesGrouped.length} Pokémon)
          </p>
          <CollapsibleList
            items={newMovesGrouped}
            initial={5}
            itemNoun="more Pokémon"
            expanded={expandNewMoves}
            onToggle={() => setExpandNewMoves((v) => !v)}
            renderItem={(g, i) => (
              // Pokémon name + moves stack vertically on narrow screens
              // and sit side-by-side on wider ones.  No truncate — long
              // move lists wrap to multiple lines rather than ellipsis-cut.
              <li key={i} className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 text-xs">
                <span className="text-white/85 font-medium sm:min-w-[7rem] shrink-0">{g.pokemon_name}</span>
                <span className="text-white/55 sm:flex-1 break-words">
                  {g.moves.map((m, j) => (
                    <span key={j}>
                      <NewMoveChip m={m} />
                      {j < g.moves.length - 1 && <span className="text-white/30">, </span>}
                    </span>
                  ))}
                </span>
              </li>
            )}
          />
        </div>
      )}

      {/* ── Cup rotation (grouped by week — multi-cup weeks render nested) ── */}
      {cupSchedule.length > 0 && (() => {
        const cupWeeks = groupCupsByWeek(cupSchedule);
        return (
          <div className="mb-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
              Cup rotation ({cupWeeks.length} week{cupWeeks.length === 1 ? "" : "s"}
              {cupSchedule.length !== cupWeeks.length && <> · {cupSchedule.length} cups</>})
            </p>
            <CollapsibleList
              items={cupWeeks}
              initial={5}
              itemNoun="more weeks"
              expanded={expandCups}
              onToggle={() => setExpandCups((v) => !v)}
              renderItem={(w, i) => <CupWeekBlock key={i} week={w} />}
            />
          </div>
        );
      })()}

      {/* ── Rank rewards (narrative) ──────────────────────────────────── */}
      {rankRewards && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
            Rank rewards
          </p>
          <p className="text-xs text-white/65 leading-relaxed">{rankRewards}</p>
        </div>
      )}

      {/* ── Extra notes ───────────────────────────────────────────────── */}
      {notes && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
            Additional details
          </p>
          <p className="text-xs text-white/60 leading-relaxed">{notes}</p>
        </div>
      )}

      <SeasonSourceLink sourceUrl={payload.source_url} />
    </CardShell>
  );
}
